import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  WaterDropIcon,
  BusinessIcon,
  BuildingIcon,
  WrenchIcon
} from '../components/Icons';
import './Homepage.css';

const Stat = ({ value, label }) => (
  <div className="trust-item">
    <div className="trust-number">{value}</div>
    <div className="trust-text">{label}</div>
  </div>
);

const Homepage = () => {
  const navigate = useNavigate();

  // ROI Calculator (kept minimal so page compiles)
  const [dailyCustomers, setDailyCustomers] = useState(40);
  const [avgLiters, setAvgLiters] = useState(10);
  const [pricePerLiter, setPricePerLiter] = useState(2.5);
  const [daysOpen, setDaysOpen] = useState(26);
  const [estimatedCapex, setEstimatedCapex] = useState(120000);

  const calc = useMemo(() => {
    const monthlyLiters = dailyCustomers * avgLiters * daysOpen;
    const monthlyRevenue = monthlyLiters * pricePerLiter;
    const breakEvenMonths = monthlyRevenue > 0 ? (estimatedCapex / monthlyRevenue) : 0;
    return { monthlyLiters, monthlyRevenue, breakEvenMonths };
  }, [dailyCustomers, avgLiters, pricePerLiter, daysOpen, estimatedCapex]);

  const onQuoteSubmit = useCallback((e) => {
    e.preventDefault();
    navigate('/request');
  }, [navigate]);

  // ----- Shopify Products Slideshow (8 slides) -----
  // Replace src/href values with your real product images + Shopify URLs.
  const slides = [
    { src: `${process.env.PUBLIC_URL}/images/1000-Litres-Per-Hour-Water-Purification-System-On-Brackish-Water-scaled.webp`, href: 'https://watershopssa.co.za/products/1000-litres-per-hour-water-purification-system-on-brackish-water', title: '1000 Litres Per Hour Water Purification System On Brackish Water' },
    { src: `${process.env.PUBLIC_URL}/images/1000-Litres-Per-Hour-Standard-Water-Purification-System-scaled.webp`, href: 'https://watershopssa.co.za/products/1000-litres-per-hour-standard-water-purification-system', title: '1000 Litres Per Hour Standard Water Purification System' },
    { src: `${process.env.PUBLIC_URL}/images/500-Litres-Per-Hour-Standard-Water-Purification-Plant-scaled.webp`, href: 'https://watershopssa.co.za/products/500-litres-per-hour-standard-water-purification-plant', title: '500 Litres Per Hour Standard Water Purification Plant' },
    { src: `${process.env.PUBLIC_URL}/images/500-Litres-Per-Hour-Water-Purification-Plant-On-Brackish-Water-scaled.webp`, href: 'https://watershopssa.co.za/products/500-litres-per-hour-water-purification-plant-on-brackish-water', title: '500 Litres Per Hour Water Purification Plant On Brackish Water' },
    { src: `${process.env.PUBLIC_URL}/images/250-Litres-Per-Hour-Water-Purification-Plant-On-Brackish-Water-scaled.webp`, href: 'https://watershopssa.co.za/products/250-litres-per-hour-water-purification-plant-on-brackish-water', title: '250 Litres Per Hour Water Purification Plant On Brackish Water' },
    { src: `${process.env.PUBLIC_URL}/images/250-Litres-Per-Hour-Standard-Water-Purification-System-scaled.webp`, href: 'https://watershopssa.co.za/products/250-litres-per-hour-standard-water-purification-system', title: '250 Litres Per Hour Standard Water Purification System' },
    { src: `${process.env.PUBLIC_URL}/images/125-Litres-Per-Hour-Water-Purification-Plant-On-Brackish-Water-scaled.webp`, href: 'https://watershopssa.co.za/products/125-litres-per-hour-water-purification-plant-on-brackish-water', title: '125 Litres Per Hour Water Purification Plant On Brackish Water' },
    { src: `${process.env.PUBLIC_URL}/images/125-Litres-Per-Hour-Standard-Water-Purification-Plant-1-scaled.webp`, href: 'https://watershopssa.co.za/products/125-litres-per-hour-standard-water-purification-plant', title: '125 Litres Per Hour Standard Water Purification Plant' },
  ];
  const [slidesPerView, setSlidesPerView] = useState(1);
  const [currentProduct, setCurrentProduct] = useState(0);

  useEffect(() => {
    const updateSlidesPerView = () => {
      const w = window.innerWidth;
      if (w < 768) return setSlidesPerView(1);
      return setSlidesPerView(1);
    };
    updateSlidesPerView();
    window.addEventListener('resize', updateSlidesPerView);
    return () => window.removeEventListener('resize', updateSlidesPerView);
  }, []);
  const totalPages = Math.max(1, Math.ceil(slides.length / slidesPerView));
  const prevProduct = () => setCurrentProduct((i) => (i - 1 + slides.length) % slides.length);
  const nextProduct = () => setCurrentProduct((i) => (i + 1) % slides.length);

  useEffect(() => {
    const id = setInterval(() => {
      setCurrentProduct((i) => (i + 1) % slides.length);
    }, 3500);
    return () => clearInterval(id);
  }, [slides.length]);

  return (
    <div className="homepage">
      <Helmet>
        <title>Pure Drop Technicals - Start Your Water Business Today</title>
        <meta
          name="description"
          content="Professional water shop installations and commercial purification plants in South Africa. Start your water business with Pure Drop. Free consultation available."
        />
        {/* Page-scoped image sizing + slider CSS */}
        <style>{`
          /* Generic small image wrapper used across cards/blocks */
          .img-wrap-sm {
            height: 160px;
            width: 100%;
            border-radius: 12px;
            overflow: hidden;
            background: #f4f7fb;
            border: 1px solid #e6eef5;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          /* Square wrapper for products slideshow (keeps 1:1 nicely) */
          .img-wrap-square {
            width: 100%;
            max-width: 600px;     /* visual cap on desktop */
            aspect-ratio: 1 / 1;  /* square */
            border-radius: 16px;
            overflow: hidden;
            background: #f5f9fd;
            border: 1px solid #e0e7ef;
            margin: 0 auto;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .img-wrap-lg {
            width: 100%;
            max-width: 900px;
            aspect-ratio: 16 / 9;
            border-radius: 16px;
            overflow: hidden;
            margin: 0 auto;
            background: #f5f9fd;
            border: 1px solid #e0e7ef;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .img-fit {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
          }

          /* Service cards spacing fixes */
          .service-card .service-media-placeholder { margin-bottom: 12px; }
          .gallery-card .img-wrap-sm { margin-bottom: 8px; }

          /* Ensure buttons render nicely even if theme overrides */
          .btn { display: inline-flex; align-items: center; justify-content: center; }

          /* Slider styles */
          .slider {
            position: relative;
            overflow: hidden;
            max-width: 1000px;
            margin: 0 auto;
            border-radius: 14px;
            border: 1px solid #e6eef5;
            background: #fff;
            box-shadow: 0 5px 24px rgba(0,0,0,0.06);
          }

          .slider-track {
            display: flex;
            transition: transform 600ms ease;
            will-change: transform;
          }

          .slide-page {
            min-width: 100%;
            display: grid;
            gap: 16px;
          }

          .slide {
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 20px;
            background: linear-gradient(180deg, #f9fbfe, #fff);
            border-radius: 12px;
          }

          .slide-title {
            font-size: 1rem;
            font-weight: 600;
            color: #003366;
            margin: 10px 0 6px;
            text-align: center;
          }

          .slide .btn {
            font-size: 0.9rem;
            padding: 8px 18px;
            border-radius: 6px;
          }

          .slider-nav {
            position: absolute;
            top: 50%;
            width: 100%;
            display: flex;
            justify-content: space-between;
            transform: translateY(-50%);
            pointer-events: none;
          }

          .slider-nav button {
            pointer-events: auto;
            background: rgba(255,255,255,0.9);
            border: 1px solid #cfd8e3;
            border-radius: 50%;
            font-size: 1.4rem;
            cursor: pointer;
            padding: 6px 12px;
            margin: 0 10px;
          }

          .slider-dots {
            display: flex;
            justify-content: center;
            margin-top: 10px;
            gap: 6px;
          }

          .slider-dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            border: none;
            background: #c9d7ea;
            cursor: pointer;
          }

          .slider-dot.active {
            background: #0077b6;
            width: 22px;
            border-radius: 10px;
          }

          @media (max-width: 768px) {
            .img-wrap-square { max-width: 400px; }
            .img-wrap-lg { max-width: 100%; }
          }

          @media (max-width: 480px) {
            .img-wrap-square { max-width: 300px; }
          }
        `}</style>
      </Helmet>

      {/* Sticky WhatsApp / Call CTA */}
      <div className="sticky-cta">
        <a
          className="pill pill-whatsapp"
          href="https://wa.me/27609382465?text=Hi%20Pure%20Drop%2C%20I%27d%20like%20a%20quote."
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
        >
          Chat on WhatsApp
        </a>
        <a className="pill pill-call" href="tel:0105000105" aria-label="Call Us">
          Call Us
        </a>
      </div>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-gradient" aria-hidden="true" />
          <div className="bubble-field" aria-hidden="true">
            <span className="bubble b1" />
            <span className="bubble b2" />
            <span className="bubble b3" />
            <span className="bubble b4" />
            <span className="bubble b5" />
            <span className="bubble b6" />
          </div>
          <div
            className="hero-gif bg-untouchable"
            aria-hidden="true"
            style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/water43.gif)` }}
          />
          <div className="container">
            <div className="hero-content">
              <div className="hero-text">
                <h1 className="fade-in-up">Start Your Water Business with Pure Drop</h1>
                <p className="hero-subtitle fade-in-up">
                  Professional water shop installations and commercial purification plants. Pure water. Pure profit. Pure Drop.
                </p>
                <div className="hero-cta fade-in-up">
                  <Link to="/request" className="btn btn-primary">Get a Free Consultation</Link>
                  <Link to="/services" className="btn btn-outline">Explore Services</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="wave-separator" aria-hidden="true">
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path d="M0,60 C320,120 1120,0 1440,60 L1440,120 L0,120 Z" />
        </svg>
      </div>

      {/* Our Water Solutions (uses your new images) */}
      <section className="services-showcase section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Our Water Solutions</h2>
            <p>Comprehensive water business solutions from setup to success</p>
          </div>

          <div className="services-grid">
            <div className="service-card">
              <div className="service-media-placeholder">
                <div className="img-wrap-sm">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/watershop.png`}
                    alt="Pure Drop Water Shop Installation"
                    className="img-fit"
                  />
                </div>
              </div>
              <div className="service-icon"><BusinessIcon size={48} className="service-icon-svg" /></div>
              <h3>Water Shop Installations</h3>
              <p>Complete turnkey water shop setups including equipment installation and business training.</p>
              <Link to="/services" className="service-link">Learn More →</Link>
            </div>

            <div className="service-card">
              <div className="service-media-placeholder">
                <div className="img-wrap-sm">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/commercialwater.png`}
                    alt="Pure Drop Commercial Purification Plant"
                    className="img-fit"
                  />
                </div>
              </div>
              <div className="service-icon"><BuildingIcon size={48} className="service-icon-svg" /></div>
              <h3>Commercial Purification Plants</h3>
              <p>Industrial-grade RO systems, ultrafiltration, and containerized treatment plants for large-scale operations.</p>
              <Link to="/services" className="service-link">Learn More →</Link>
            </div>

            <div className="service-card">
              <div className="service-media-placeholder">
                <div className="img-wrap-sm">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/alkalinewater.png`}
                    alt="Pure Drop Alkaline Water System"
                    className="img-fit"
                  />
                </div>
              </div>
              <div className="service-icon"><WaterDropIcon size={48} className="service-icon-svg" /></div>
              <h3>Alkaline Water Systems</h3>
              <p>Health-focused water systems and filters for premium product lines.</p>
              <Link to="/services" className="service-link">Learn More →</Link>
            </div>

            <div className="service-card">
              <div className="service-media-placeholder">
                <div className="img-wrap-sm">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/support.png`}
                    alt="Pure Drop Maintenance & Support"
                    className="img-fit"
                  />
                </div>
              </div>
              <div className="service-icon"><WrenchIcon size={48} className="service-icon-svg" /></div>
              <h3>Maintenance & Support</h3>
              <p>Service plans, parts supply, technical training, and 24/7 emergency support.</p>
              <Link to="/services" className="service-link">Learn More →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Water Shop Products – Automatic Slideshow with 8 slides + Shop Now buttons */}
      <section className="shopify-products section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Water Shop Installations</h2>
            <p>Browse Water Shop Installation Specials.</p>
          </div>

          <div className="slider" aria-roledescription="carousel">
            <div
              className="slider-track"
              style={{
                transform: `translateX(-${currentProduct * 100}%)`,
                transition: 'transform 600ms ease'
              }}
            >
              {slides.map((s, i) => (
                <div
                  className="slide"
                  key={i}
                  aria-label={`Product ${i + 1} of ${slides.length}`}
                >
                  <div className="img-wrap-square">
                    <img src={s.src} alt={s.title} className="img-fit" />
                  </div>
                  <div className="slide-title">{s.title}</div>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Shop Now
                  </a>
                </div>
              ))}
            </div>
            <div className="slider-nav" aria-hidden="false">
              <button onClick={prevProduct} aria-label="Previous product">‹</button>
              <button onClick={nextProduct} aria-label="Next product">›</button>
            </div>
          </div>

          <div className="slider-dots" role="tablist" aria-label="Slide dots">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`slider-dot ${currentProduct === i ? 'active' : ''}`}
                onClick={() => setCurrentProduct(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-selected={currentProduct === i}
                role="tab"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Workshop Video (static small image placeholder) */}
      <section className="workshop-video section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Visit Our Workshop</h2>
            <p>Come visit our workshop and see our systems in action</p>
          </div>
          <div className="video-card">
            <div className="img-wrap-lg">
              <img
                src={`${process.env.PUBLIC_URL}/images/workshop.png`}
                alt="Pure Drop Workshop"
                className="img-fit"
              />
            </div>
            <div className="video-cta" style={{ marginTop: 12 }}>
              <Link to="/contact" className="btn btn-outline">Book a Workshop Visit</Link>
            </div>
            <div className="map-embed">
              <iframe
                title="Pure Drop Technicals Workshop Location"
                src="https://www.google.com/maps?q=138%20Walton%20Ave,%20Carlswald,%20Midrand,%201684&output=embed"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="map-address">
                138 Walton Ave, Carlswald, Midrand, 1684
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Installations (each with small thumbnail) */}
      <section className="projects section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Recent Installations</h2>
            <p>A snapshot of our work across SA</p>
          </div>

          <div className="gallery-grid">
            {[
              { label: 'Water Shops — Polokwane', src: `${process.env.PUBLIC_URL}/images/Water shop -  Plokwane.jpeg` },
              { label: 'Water Shops — Gauteng', src: `${process.env.PUBLIC_URL}/images/Water Shop - Gauteng.jpeg` },
              { label: 'Water Shops — Cape Town', src: `${process.env.PUBLIC_URL}/images/Water Shop - Cape Town .jpeg` },
              { label: 'Water Shops — KZN', src: `${process.env.PUBLIC_URL}/images/Water Shop - KZN.jpeg` },
            ].map(({ label, src }) => (
              <div className="gallery-card" key={label}>
                <div className="img-wrap-sm">
                  <img
                    src={src}
                    alt={label}
                    className="img-fit"
                  />
                </div>
                <h5>{label}</h5>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="trust-signals section-sm">
        <div className="container">
          <div className="trust-grid">
            <Stat value="15+" label="Years Experience" />
            <Stat value="500+" label="Successful Installations" />
            <Stat value="100%" label="Compliance Standards" />
            <Stat value="24/7" label="Support Available" />
          </div>
        </div>
      </section>

      {/* Quick ROI (kept compact) */}
      <section className="roi section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Water Shop ROI Calculator</h2>
            <p>Estimate monthly revenue and break-even timeline (rough guide)</p>
          </div>

          <div className="roi-grid">
            <div className="roi-form">
              <div className="form-row">
                <label>Daily customers</label>
                <input type="number" min="0" value={dailyCustomers} onChange={(e) => setDailyCustomers(Number(e.target.value))} />
              </div>
              <div className="form-row">
                <label>Average liters per customer</label>
                <input type="number" min="0" value={avgLiters} onChange={(e) => setAvgLiters(Number(e.target.value))} />
              </div>
              <div className="form-row">
                <label>Price per liter (ZAR)</label>
                <input type="number" min="0" step="0.1" value={pricePerLiter} onChange={(e) => setPricePerLiter(Number(e.target.value))} />
              </div>
              <div className="form-row">
                <label>Days open per month</label>
                <input type="number" min="0" max="31" value={daysOpen} onChange={(e) => setDaysOpen(Number(e.target.value))} />
              </div>
              <div className="form-row">
                <label>Estimated CAPEX (ZAR)</label>
                <input type="number" min="0" value={estimatedCapex} onChange={(e) => setEstimatedCapex(Number(e.target.value))} />
              </div>
              <p className="roi-note">*Estimates only. We’ll provide a tailored feasibility study for your site.</p>
            </div>

            <div className="roi-result">
              <div className="result-card">
                <h3>Your Estimate</h3>
                <div className="result-grid">
                  <div>
                    <span className="result-label">Monthly liters</span>
                    <div className="result-value">{calc.monthlyLiters.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="result-label">Monthly revenue</span>
                    <div className="result-value">R {calc.monthlyRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div>
                    <span className="result-label">Break-even (months)</span>
                    <div className="result-value">{calc.breakEvenMonths.toFixed(1)}</div>
                  </div>
                </div>
                <Link to="/request" className="btn btn-primary btn-full">Get a Detailed Feasibility →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta section">
        <div className="container final-cta-box">
          <h2>Ready to open your water shop?</h2>
          <p>We’ll spec the right plant, install it fast, and train your team to run profitably from day one.</p>
          <div className="hero-cta">
            <Link to="/request" className="btn btn-primary" role="button">Get a Free Consultation</Link>
            <Link to="/services" className="btn btn-outline" role="button">Compare Solutions</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;
